import { axios, getResponse, isUndNullEmp, settings } from '../../helpers/main';
import { userAuthContextType } from '../../auth/AuthProvider';

const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectServices = getResponse({ type: 0 }); //eslint-disable-line

export const UserService = {
    /**
     * Obtiene todos los usuarios que no son analistas.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    getAllNotAnalystsAsync: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.getAllNotAnalystsAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    languageId: languageId,
                    method: settings?.backend?.methods?.users?.getAllNotAnalysts,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.users}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Obtiene un usuario por ID con acceso a sus empresas relacionadas.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {String} params.id - ID del usuario.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    getByIdWithCompaniesAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.getByIdWithCompaniesAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    id: id,
                    languageId: languageId,
                    method: settings?.backend?.methods?.users?.getByIdWithCompaniesAccess,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.users}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Crea un nuevo usuario.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {Object} params.data - Objeto usuario con los datos a crear.
     * @param {Object} params.roleUser - Objeto con la información del rol asociado.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    addAsync: async ({
        auth,
        data,
        roleUser,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.addAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    data: data,
                    roleUser: roleUser,
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.users}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Actualiza un usuario existente.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {String} params.id - ID del usuario a actualizar.
     * @param {Object} params.data - Objeto usuario con los nuevos datos.
     * @param {Object} params.roleUserBefore - Rol anterior del usuario.
     * @param {Object} params.roleUserAfter - Nuevo rol del usuario.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    update: async ({
        auth,
        id,
        data,
        roleUserBefore,
        roleUserAfter,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.update';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    data: data,
                    roleUserBefore: roleUserBefore,
                    roleUserAfter: roleUserAfter,
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.users}/${id}`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Elimina un usuario por su ID.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {String} params.id - ID del usuario a eliminar.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.deleteAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.users}/${id}`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.delete(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Obtiene los datos generales de un usuario (empresas, clientes, roles) por ID.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {String} params.id - ID del usuario.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    getGeneralDataAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.getGeneralDataAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    id: id,
                    method: settings?.backend?.methods?.users?.getByIdWithCompaniesAccess,
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.users}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Asigna una empresa a un usuario.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {Object} params.data - Objeto con la información de la asignación.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    addCompanyAssignmentAsync: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.addCompanyAssignmentAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    data: data,
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.userCompanies}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Elimina una asignación de empresa de un usuario.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {String} params.id - ID de la asignación a eliminar.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    deleteCompanyAssignmentAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.deleteCompanyAssignmentAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.userCompanies}/${id}`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.delete(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Asigna un cliente (bajo una empresa) a un usuario.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {Object} params.data - Objeto con la información de la asignación.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    addCustomerAssignmentAsync: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.addCustomerAssignmentAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    data: data,
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.userCompanyCustomers}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Elimina una asignación de cliente de un usuario.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {String} params.id - ID de la asignación a eliminar.
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    deleteCustomerAssignmentAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.deleteCustomerAssignmentAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.userCompanyCustomers}/${id}`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.delete(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },

    /**
     * Obtiene los clientes con detalles asociados a una asignación de usuario-empresa.
     * @param {Object} params - Parámetros de la función.
     * @param {authServices} params.auth - Objeto de autenticación distribuido por useAuth.
     * @param {String} params.id - ID de la asignación usuario-empresa (userCompanyId).
     * @param {Number} params.languageId - ID del lenguaje actual.
     * @param {String} [params.subMethod] - Nombre del sub-método para trazabilidad (opcional).
     * @returns {Promise<objectServices>} Resultado estandarizado vía getResponse.
     */
    getCustomersWithDetailsByUserCompanyIdAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'UserService.getCustomersWithDetailsByUserCompanyIdAsync';
        let response = getResponse({ type: 0 });
        try {
            const requestObj = {
                headers: {
                    authorization: `bearer ${auth?.user?.token?.token}`,
                },
                data: {
                    id: id,
                    method: settings?.backend?.methods?.userCompanyCustomers?.getAllWithDetailsByUserCompanyId,
                    languageId: languageId,
                },
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.userCompanyCustomers}/`,
                subMethod: subMethod || currentMethod,
            };
            response = await axios?.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    },
};
